import * as Sentry from '@sentry/browser'
import { type z } from 'zod'

import { type schemas } from '~api/apiZodios'
import { formatPhoneNumber } from '~components/internal'
import {
  type BoolValue0,
  type ContactBookId,
  type DatetimeValue0,
  type FileValue0,
  type FormDataNode,
  FormNodeType,
  type FormNodeTypeEnum,
  type Identity2,
  type IdentityFormData,
  type IdentityValue0,
  type InputId,
  type InputSubstep2,
  type LocationValue0,
  type NumberStep,
  type NumberValue0,
  type SignatureValue0,
  type TextValue0,
  type Value0,
  type ValueCreate,
  ValueTypeEnum,
} from '~types'
import { isValidEmail } from '~utils/emailUtils'

export type ContactView = z.infer<typeof schemas.ContactView>

export function contactViewToIdentityFormData(contactView: ContactView): IdentityFormData {
  const address = contactView.addresses[0]
  return {
    first_name: contactView.first_name || null,
    last_name: contactView.last_name || null,
    email: contactView.emails[0] || null,
    phone: contactView.phones[0] ? formatPhoneNumber(contactView.phones[0]) : null,
    date_of_birth: contactView.date_of_birth || null,
    address_street: address?.street || null,
    address_city: address?.city || null,
    address_state: address?.state || null,
    address_zip: address?.zip || null,
  }
}

// Staffers live outside the contact book (they're Identity/EndUser records, not ContactRecords),
// so they never show up in contacts_searchContactDocuments results. This adapts a staffer's
// Identity2 into the ContactView shape so it can be rendered/selected alongside contact-book
// results in the identity picker. The `staffer_` id prefix (mirroring the existing `vc_` virtual
// contact convention) lets callers resolve the identity directly instead of treating it as a
// contact_record_id.
export function identity2ToContactView(identity: Identity2): ContactView {
  return {
    id: `staffer_${identity.id}`,
    contact_book_id: '' as ContactBookId,
    handle: identity.handle,
    contact_type: 'person',
    first_name: identity.first_name || null,
    last_name: identity.last_name || null,
    date_of_birth: identity.date_of_birth ?? null,
    emails: identity.emails.map((email) => email.address),
    phones: identity.phones,
    addresses: [],
    tags: [],
    attributions: [],
    interaction_count: 0,
  }
}

export function identityValueToFormState(identityValue: IdentityValue0): IdentityFormData {
  return {
    first_name: identityValue.first_name ?? null,
    last_name: identityValue.last_name ?? null,
    email: identityValue.email ?? null,
    phone: identityValue.phone ? formatPhoneNumber(identityValue.phone) : null,
    date_of_birth: identityValue.date_of_birth ?? null,
    address_street: identityValue.address_street ?? null,
    address_city: identityValue.address_city ?? null,
    address_state: identityValue.address_state ?? null,
    address_zip: identityValue.address_zip ?? null,
  }
}

export function value0ToValueCreate(value: Value0): ValueCreate {
  switch (value.type) {
    case ValueTypeEnum.enum.bool:
      return { bool_value: (value.bool_value as BoolValue0).checked }
    case ValueTypeEnum.enum.datetime:
      return { datetime_value: (value.datetime_value as DatetimeValue0).datetime }
    case ValueTypeEnum.enum.file:
      return { file_value: (value.file_value as FileValue0).files.join(', ') }
    case ValueTypeEnum.enum.identity: {
      const identityValue0 = value.identity_value as IdentityValue0
      return {
        identity_value: identityValue0.identity,
        identity_snapshot: identityValueToFormState(
          identityValue0,
        ) as ValueCreate['identity_snapshot'],
      }
    }
    case ValueTypeEnum.enum.location:
      return { location_value: (value.location_value as LocationValue0).location }
    case ValueTypeEnum.enum.number:
      return { number_value: (value.number_value as NumberValue0).number }
    case ValueTypeEnum.enum.signature:
      return { signature_value: (value.signature_value as SignatureValue0).signature }
    case ValueTypeEnum.enum.text:
      return { text_value: (value.text_value as TextValue0).text }
    default:
      throw new Error(`Unknown value type: ${value.type}`)
  }
}

export function createFormDataNode(
  id: string, // frontend generated instance id
  type: FormNodeTypeEnum,
  value: ValueCreate | null,
  parentSectionId: string | null, // frontend generated instance id for add an item
  lastSavedInput: InputId | null,
): FormDataNode {
  const dataValue = value ?? (type === FormNodeType.enum.input ? {} : null)

  return {
    id: id,
    type: type,
    value: dataValue,
    parent_section: parentSectionId,
    last_saved_input: lastSavedInput,
  }
}

export function validateBooleanInstances(instances: FormDataNode[]): boolean {
  return instances.every((instance) => {
    const value = instance.value
    return !!(value && value.bool_value === true)
  })
}

export function validateDatetimeInstances(instances: FormDataNode[]): boolean {
  return instances.every((instance) => {
    const value = instance.value
    return !!(
      value &&
      value.datetime_value !== null &&
      value.datetime_value !== undefined &&
      value.datetime_value !== ''
    )
  })
}

export function validateBasicIdentityInstances(instances: FormDataNode[]): boolean {
  // this just checks whether an id is present, it does not check whether there is sufficient
  // data on the identity object
  return instances.every((instance) => {
    const value = instance.value
    return !!(
      value &&
      value.identity_value !== null &&
      value.identity_value !== undefined &&
      value.identity_value !== ''
    )
  })
}

export function validateFileInstances(instances: FormDataNode[]): boolean {
  return instances.every((instance) => {
    const value = instance.value
    return !!(
      value &&
      value.file_value !== null &&
      value.file_value !== undefined &&
      value.file_value !== ''
    )
  })
}

export function validateLocationInstances(instances: FormDataNode[]): boolean {
  return instances.every((instance) => {
    const value = instance.value
    return !!(
      value &&
      value.location_value !== null &&
      value.location_value !== undefined &&
      value.location_value !== ''
    )
  })
}

export function validateSignatureInstances(instances: FormDataNode[]): boolean {
  return instances.every((instance) => {
    const value = instance.value
    return !!(
      value &&
      value.signature_value !== null &&
      value.signature_value !== undefined &&
      value.signature_value !== ''
    )
  })
}

export function hasXDecimalPlaces(num: string, x: number): boolean {
  num = num.replace(/\.?0+$/, '') // stripe trailing zeros
  if (x < 0) return false
  if (isNaN(Number(num))) return false

  if (x === 0) {
    return !num.includes('.')
  }

  if (num.includes('e') || num.includes('E')) {
    return false // For simplicity, reject scientific notation
  }

  const parts = num.split('.')

  if (parts.length === 1) {
    return true // Whole numbers are valid for any decimal places setting
  }

  return parts[1].length <= x // Allow at most x decimal places
}

export function validateNumberInstances(
  numberStep: NumberStep,
  instances: FormDataNode[],
): boolean {
  return instances.every((instance) => {
    const value = instance.value
    if (!(value && value.number_value !== null && value.number_value !== undefined)) {
      return false
    } else if (!numberStep.allow_negative_numbers && Number(value.number_value) < 0) {
      return false
    } else if (!numberStep.allow_zero && Number(value.number_value) === 0) {
      return false
    } else if (
      numberStep?.decimal_places !== null &&
      numberStep?.decimal_places !== undefined &&
      !hasXDecimalPlaces(value.number_value, numberStep.decimal_places)
    ) {
      return false
    } else {
      return true
    }
  })
}

export function validateTextInstances(instances: FormDataNode[]): boolean {
  return instances.every((instance) => {
    const value = instance.value
    return !!(
      value &&
      value.text_value !== null &&
      value.text_value !== undefined &&
      value.text_value !== ''
    )
  })
}
export async function validateInput(
  input: InputSubstep2,
  instances: FormDataNode[],
  identityFormDataFresh: Record<string, IdentityFormData> | undefined,
): Promise<boolean> {
  if (!input.required) return true
  if (input.type === ValueTypeEnum.enum.bool) {
    return validateBooleanInstances(instances ?? [])
  } else if (input.type === ValueTypeEnum.enum.datetime) {
    return validateDatetimeInstances(instances ?? [])
  } else if (input.type === ValueTypeEnum.enum.file) {
    return validateFileInstances(instances ?? [])
  } else if (input.type === ValueTypeEnum.enum.identity) {
    const identityStep = input.sub?.identity_step_sub
    const isValid = (item: string | null | undefined): boolean => {
      return item !== null && item !== undefined && item !== ''
    }

    return instances.every((instance: FormDataNode) => {
      const data = identityFormDataFresh ? identityFormDataFresh[instance.id] : undefined

      if (data?.emailOverride) {
        return isValidEmail(data.emailOverride)
      }

      if (!data?.isVirtualContact && !validateBasicIdentityInstances([instance])) {
        Sentry.addBreadcrumb({
          category: 'form.validation',
          message: 'Identity failed form validation',
          level: 'warning',
          data: { identityFormData: data, instance },
        })
        return false
      }

      if (identityStep?.require_address && !isValid(data?.address_street)) {
        Sentry.addBreadcrumb({
          category: 'form.validation',
          message: 'Identity address failed form validation',
          level: 'warning',
          data: { identityFormData: data },
        })
        return false
      }
      if (identityStep?.require_date_of_birth && !isValid(data?.date_of_birth)) {
        Sentry.addBreadcrumb({
          category: 'form.validation',
          message: 'Identity DOB failed form validation',
          level: 'warning',
          data: { identityFormData: data },
        })
        return false
      }
      if (identityStep?.require_email && !isValidEmail(data?.email ?? '')) {
        Sentry.addBreadcrumb({
          category: 'form.validation',
          message: 'Identity Email failed form validation',
          level: 'warning',
          data: { identityFormData: data },
        })
        return false
      }
      if (
        identityStep?.require_name &&
        !isValid((data?.first_name ?? '') + (data?.last_name ?? ''))
      ) {
        Sentry.addBreadcrumb({
          category: 'form.validation',
          message: 'Identity Name failed form validation',
          level: 'warning',
          data: { identityFormData: data },
        })
        return false
      }
      if (identityStep?.require_phone && !isValid(data?.phone)) {
        Sentry.addBreadcrumb({
          category: 'form.validation',
          message: 'Identity Phone failed form validation',
          level: 'warning',
          data: { identityFormData: data },
        })
        return false
      }
      return true
    })
  } else if (input.type === ValueTypeEnum.enum.location) {
    return validateLocationInstances(instances ?? [])
  } else if (input.type === ValueTypeEnum.enum.number) {
    const numberStep = input.sub?.number_step_sub
    if (!numberStep) return true
    return validateNumberInstances(numberStep, instances ?? [])
  } else if (input.type === ValueTypeEnum.enum.signature) {
    return validateSignatureInstances(instances ?? [])
  } else if (input.type === ValueTypeEnum.enum.text) {
    return validateTextInstances(instances ?? [])
  }
  return true
}
